<template>
  <!-- 主容器 -->
  <div class="jump-modal-page">
    <!-- 内容区域 -->
    <div class="page-content">
      <!-- 错误状态显示 -->
      <div class="error-section" v-if="patientDataError">
        <div class="error-icon">⚠️</div>
        <div class="error-text">{{ errorMessage }}</div>
        <div class="error-actions">
          <el-button type="danger" @click="closeModal">
            <span class="btn-icon">✕</span>关闭页面
          </el-button>
        </div>
      </div>

      <!-- 患者信息显示 -->
      <div class="patient-info" v-if="currentPatient && !patientDataError">
        <div class="info-row">
          <span class="label">患者姓名：</span>
          <span class="value">{{ currentPatient.realname || '-' }}</span>
          <span class="separator">|</span>
          <span class="label">就诊号：</span>
          <span class="value">{{ visitNum || '-' }}</span>
        </div>
      </div>

      <!-- 录音组件 -->
      <div class="recording-section" v-if="!recordingCompleted && !patientDataError">
          <!-- 不携带病例 -->
          <JumpModalSoundRecording
            ref="soundRecordingCrt"
            @handleShibie="(audioBlob) => handleShibie(audioBlob, false)" 
            :patientSelected="patientSelected"
            :medmstrId="currentPatient?.id"
            start-button-text="新建录音"
            start-button-tip="为该患者新建一个病历并开始录音"
          />
      </div>

      <!-- 录音处理中提示 -->
      <div class="processing-tip" v-if="recordingProcessing && !patientDataError">
        正在处理录音，请稍等...
        <JumpModalSoundRecording
          ref="soundRecording"
          @handleShibie="(audioBlob) => handleShibie(audioBlob, true)"
          :patientSelected="patientSelected"
          :medmstrId="currentPatient?.id"
        />
      </div>

      <!-- 提交按钮（在病历处理完成后显示） -->
      <div class="action-buttons" v-if="recordingCompleted && archivesProcessed && !hisSubmitted && !patientDataError">
        <!-- 新增 flex 容器包裹两个录音组件 -->
        <div class="sound-recording-wrapper">
          <div class="submit-button-large-wrapper">
            <el-button
              type="primary"
              @click="handleSubmitHis"
              :loading="hisSubmitLoading"
              class="submit-button-large"
            >
              <i class="el-icon-upload2"></i>
              提交HIS
            </el-button>
            <!-- 测试按钮（开发环境使用，生产环境已隐藏） -->
            <!-- <el-button type="success" @click="handleTestClose">
              <span class="btn-icon">🧪</span>测试关闭窗口
            </el-button> -->
          </div>
          <!-- 携带病例 -->
          <JumpModalSoundRecording
            ref="soundRecording"
            @handleShibie="(audioBlob) => handleShibie(audioBlob, true)" 
            :patientSelected="patientSelected"
            :medmstrId="currentPatient?.id"
            start-button-text="追加录音"
            start-button-tip="为当前患者在当前病历上用录音追加内容"
          />
        </div>
      </div>

      <!-- 录音内容展示和编辑 -->
      <div class="recording-result" v-if="recordingCompleted && !hisSubmitted && !patientDataError">
        <!-- 病历处理中提示 -->
        <div v-if="archivesProcessing" class="processing-tip">
          正在分解录音内容为病历字段，请稍等...
        </div>
        <div class="result-layout">
          <div class="result-column left-column">
            <div class="section-title">
              录音识别结果
            </div>
            <div class="extracted-content-box">
              <div class="extracted-text-wrapper">
                <div class="extracted-text">{{ extractedMessage }}</div>
              </div>
            </div>
          </div>
          <div class="result-column right-column">
            <div class="section-title">
              岚小医提示
            </div>
            <div class="extracted-content-box">
              <div class="extracted-text-wrapper">
                <div class="extracted-text">{{ tipMessage }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 提交成功提示（与 jumpModal-copy 文案一致） -->
      <div class="submit-success" v-if="hisSubmitted && !patientDataError">
        <div class="success-icon">✓</div>
        <div class="success-text">提交成功！</div>
      </div>
    </div>
  </div>
</template>

<script>
import JumpModalSoundRecording from "@/components/jumpModalSoundRecording.vue";
import { request } from "@/utils/request";
import { createConversationLy } from "@/services/conversation/index";
import { mapState } from "vuex";
import { Button, Loading } from "element-ui";

/**
 * 仅开发环境：HIS 直连失败/异常时额外提示并在 2s 后关窗，便于本地验证 window.open 子窗。
 * 生产与 jumpModal-copy 一致，不增加任何关窗或文案。
 */
const DEMO_CLOSE_ON_HIS_SUBMIT_ERROR = process.env.NODE_ENV === 'development';

export default {
  name: 'JumpModal',
  components: {
    JumpModalSoundRecording,
    'el-button': Button
  },
  props: {
    // 是否显示模态窗口
    visible: {
      type: Boolean,
      default: false
    },
    // 模态窗口标题
    title: {
      type: String,
      default: '快速问诊'
    },
    // 模态窗口宽度
    modalWidth: {
      type: Number,
      default: 450
    },
    // 模态窗口高度
    modalHeight: {
      type: Number,
      default: 500
    },
    // 写死的患者ID（用于自动选择患者）
    fixedPatientId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 当前选中的患者
      currentPatient: null,
      // 就诊号
      visitNum: localStorage.getItem('visitNum') || '',
      // 录音内容
      audioMessage: '',
      // 提取内容
      extractedMessage: '',
      // 提示内容
      tipMessage: '',
      // 是否完成录音
      recordingCompleted: false,
      // 是否已提交HIS
      hisSubmitted: false,
      // HIS提交加载状态
      hisSubmitLoading: false,
      // 是否自动开始录音
      autoStartRecording: false,
      // 是否已选择患者（通过 by-visit-num 接口）
      patientSelected: false,
      // 病历字段数据
      activeArchives: [],
      // 病历保存状态（防止重复调用）
      isSavingArchives: false,
      // 病历处理状态
      archivesProcessing: false,
      // 病历处理完成状态
      archivesProcessed: false,
      // 获取患者数据失败状态
      patientDataError: false,
      // 错误信息
      errorMessage: '',
      // 开发环境标识（控制测试按钮显示）
      isDevelopment: process.env.NODE_ENV === 'development'
    };
  },
  mounted() {
    // 组件挂载时自动初始化
    this.initializeModal();
  },
  methods: {
    // 确保存在 ChatId，如果不存在则创建
    async ensureChatId() {
      // 如果已有chatId，直接返回
      if (this.currentPatient?.chatId && this.currentPatient.chatId.trim() !== '') {
        console.log('jumpModal: chatId已存在，直接使用:', this.currentPatient.chatId);
        return this.currentPatient.chatId;
      }

      // 没有chatId，需要创建会话
      console.log('jumpModal: chatId不存在，需要创建会话');
      try {
        const result = await createConversationLy({
          medmstrId: this.currentPatient?.id,
          professionalId: localStorage.getItem('userId') || this.userInfo?.id,
          departmentId: this.getCurrentDepartmentId(),
          visitNum: this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || ''
        });

        if (result.success && result.result?.chatId) {
          this.currentPatient.chatId = result.result.chatId;
          console.log('jumpModal: 会话创建成功，chatId:', this.currentPatient.chatId);
          return this.currentPatient.chatId;
        } else {
          console.error('jumpModal: 创建会话失败:', result?.message || '未知错误');
          // 创建会话失败，返回null但不抛出错误，让页面可以继续
          return null;
        }
      } catch (error) {
        console.error('jumpModal: 创建会话异常:', error);
        // 创建会话异常，返回null但不抛出错误
        return null;
      }
    },

    // 初始化模态窗口
    async initializeModal() {
      try {
        console.log('JumpModal: 开始初始化...');

        // 自动选择患者
        await this.selectFixedPatient();

        // 【修复】检查是否有有效的chatId，如果没有则不自动录音
        if (this.autoStartRecording && !this.patientDataError && this.currentPatient?.chatId) {
          setTimeout(() => {
            console.log('JumpModal: 开始自动录音...');
            this.startAutoRecording();
          }, 1000);
        } else if (!this.currentPatient?.chatId) {
          console.log('JumpModal: 没有有效的chatId，不自动录音');
        }
      } catch (error) {
        console.error('初始化模态窗口失败:', error);
        // 不显示错误提示，让错误状态显示组件来处理
      }
    },

    // 选择写死的患者
    async selectFixedPatient() {
      // 从localStorage获取就诊号和医生ID
      const visitNum = localStorage.getItem('visitNum');
      const professionalId = localStorage.getItem('userId') || this.userInfo?.id;

      if (!visitNum) {
        console.error('JumpModal: 未找到visitNum');
        this.patientDataError = true;
        this.errorMessage = '未找到就诊号，请关闭页面重试';
        throw new Error('未找到visitNum');
      }

      if (!professionalId) {
        console.error('JumpModal: 未找到professionalId');
        this.patientDataError = true;
        this.errorMessage = '未找到医生信息，请关闭页面重试';
        throw new Error('未找到professionalId');
      }

      // 从接口获取患者完整信息
      try {
        console.log('JumpModal: 尝试获取患者信息...', { visitNum, professionalId });
        const response = await request({
          url: '/ly/patient/by-visit-num',
          method: 'POST',
          data: {
            visitNum: visitNum,
            professionalId: professionalId
          }
        });

        if (response && response.success && response.result) {
          console.log('JumpModal: 获取到患者信息:', response.result);
          this.currentPatient = response.result;
          this.patientSelected = true;

          // 【修复】获取患者信息后立即检查并创建会话
          const chatId = await this.ensureChatId();
          if (!chatId) {
            console.error('JumpModal: 会话创建失败');
            this.patientDataError = true;
            this.errorMessage = '会话创建失败，请关闭页面重试';
            throw new Error('会话创建失败');
          }
        } else {
          console.error('JumpModal: 获取患者信息失败:', response?.message || '未知错误');
          this.patientDataError = true;
          this.errorMessage = '获取患者数据失败：' + (response?.message || '未知错误') + '，请关闭页面重试';
          throw new Error('获取患者信息失败');
        }
      } catch (error) {
        console.error('JumpModal: 获取患者信息失败:', error);
        this.patientDataError = true;
        this.errorMessage = '获取患者数据失败：' + (error.message || '网络异常') + '，请关闭页面重试';
        throw error;
      }
    },

    // 测试关闭弹出窗口（用于测试）
    async handleTestClose() {
      console.log('🧪 测试：模拟 HIS 提交成功并关闭窗口');

      // 先保存病历
      const saveResult = await this.saveArchives();
      if (!saveResult) {
        this.$message.warning('测试：病历保存失败');
        return;
      }

      console.log('测试：病历保存成功');
      this.hisSubmitted = true;
      this.$message.success('测试：模拟提交成功！2秒后将关闭窗口');

      // 2秒后自动关闭窗口
      setTimeout(() => {
        this.closeModal();
      }, 2000);
    },

    // 自动开始录音
    startAutoRecording() {
      if (this.$refs.soundRecording) {
        this.$refs.soundRecording.startRecording();
      }
    },

    // 处理录音识别
    async handleShibie(audioBlob, includeMedicalRecords = false) {
      if (!audioBlob) {
        return this.$message.warning("没有录音数据");
      }

      // 显示loading状态
      const loadingInstance = this.$loading({
        lock: true,
        text: '正在识别录音，请稍等...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
        customClass: 'jump-modal-loading'
      });

      try {
        // 确保存在 ChatId
        const chatId = await this.ensureChatId();
        if (!chatId) {
          this.$message.error("会话创建失败，无法上传录音");
          return;
        }

        // 构建上传参数
        const formData = new FormData();
        const fileName = `医患对话录音_${new Date().getTime()}.webm`;
        formData.append('file', audioBlob, fileName);
        const name = `医患对话录音_${new Date().toLocaleString()}`;
        formData.append('name', name);
        const description = '医生与患者对话录音';
        formData.append('description', description);
        const category = 'consultation';
        formData.append('category', category);
        const tags = '医患对话,症状描述,问诊记录';
        formData.append('tags', tags);

        const departmentId = this.getCurrentDepartmentId();
        const visitNum = this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || '';
        // 添加会话相关信息
        formData.append('chatId', chatId);
        formData.append('medmstrId', this.currentPatient.id);
        formData.append('role', 'doctor');
        formData.append('departmentId', departmentId);
        formData.append('visitNum', visitNum);

        // 调用统一的上传接口
        const resp = await request({
          url: '/ly/midRecord/upload',
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (resp && resp.success) {
          this.audioMessage = resp.result?.audioMessage || '';
          this.extractedMessage = resp.result?.extractedMessage || '';

          // 关闭loading
          loadingInstance.close();

          // 标记录音完成
          this.recordingCompleted = true;

          // 1. 立即发起send请求（独立异步，不阻塞后续代码）
          const sendMessageRequest = async () => {
            const response = await request({
              url: '/ly/conversation/send',
              method: 'POST',
              data: {
                chatId: chatId,
                departmentId: departmentId,
                message: this.extractedMessage,
                role: 'doctor',
                visitNum: visitNum
              }
            });
            if (response && response.success) {
              this.tipMessage = response.result.aiReturnMsg ? response.result.aiReturnMsg : response.result;
            }
          };
          // 执行send请求（无await，直接启动）
          sendMessageRequest();

          // 自动处理录音内容，分解为病历字段
          await this.processRecordingToArchives(includeMedicalRecords);
        } else {
          loadingInstance.close();
          this.$message.error("语音识别失败：" + (resp?.message || "未知错误"));

          // 保存失败的录音记录
          await this.saveFailedRecordingToStorage(audioBlob, {
            fileName,
            name,
            description,
            category,
            tags,
            chatId: chatId,
            medmstrId: this.currentPatient.id,
            role: 'doctor',
            error: resp?.message || "未知错误"
          });
        }
      } catch (error) {
        loadingInstance.close();
        console.error("录音识别过程出错：", error);
        this.$message.error("录音识别失败，请重试");

        // 保存失败的录音记录
        try {
          const chatId = await this.ensureChatId();
          await this.saveFailedRecordingToStorage(audioBlob, {
            fileName: `录音_${new Date().getTime()}.webm`,
            name: `快速问诊录音_${new Date().toLocaleString()}`,
            description: '快速问诊录音',
            category: 'quick_consultation',
            tags: '快速问诊,症状描述',
            chatId: chatId,
            medmstrId: this.currentPatient.id,
            role: 'doctor',
            error: error.message || "网络异常"
          });
        } catch (ensureError) {
          console.error('确保 ChatId 失败:', ensureError);
          // 即使确保 ChatId 失败，也尝试保存记录
          await this.saveFailedRecordingToStorage(audioBlob, {
            fileName: `录音_${new Date().getTime()}.webm`,
            name: `快速问诊录音_${new Date().toLocaleString()}`,
            description: '快速问诊录音',
            category: 'quick_consultation',
            tags: '快速问诊,症状描述',
            chatId: this.currentPatient.chatId || '',
            medmstrId: this.currentPatient.id,
            role: 'doctor',
            error: error.message || "网络异常"
          });
        }
      }
    },

    // 发送消息（复用原组件逻辑）
    async sendBtn_doctor(content) {
      if (!content) return;
      
      try {
        // 确保存在 ChatId
        const chatId = await this.ensureChatId();
        if (!chatId) {
          this.$message.error("会话创建失败，无法发送消息");
          return;
        }

        const response = await request({
          url: '/ly/conversation/send',
          method: 'POST',
          data: {
            chatId: chatId,
            departmentId: this.getCurrentDepartmentId(),
            message: content,
            role: 'doctor',
            visitNum: this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || ''
          }
        });

        if (response && response.success) {
          this.tipMessage = response.result;
        }
      } catch (error) {
        console.error('发送消息失败:', error);
        throw error;
      }
    },

    // 保存病历（调用 /ly/archive/add）
    async saveArchives(copyFlag = false) {
      // 防重复调用检查
      if (this.isSavingArchives) {
        console.log('病历正在保存中，跳过重复调用');
        return;
      }

      if (!this.currentPatient?.id) {
        return this.$message.warning("请选择患者");
      }
      if (!this.activeArchives || this.activeArchives.length === 0) {
        return this.$message.warning("暂无病例详情可保存");
      }

      // 从 activeArchives 构建 content JSON 对象（动态字段）
      const contentObj = {};
      this.activeArchives.forEach(item => {
        // 将驼峰 key 转为下划线（与后端字段一致，如 chief_complaint）
        const snakeKey = this.toSnakeCase(item.key);
        const value = item.value || '';
        // 仅收集有值的字段，避免传递空字段
        if (value && String(value).trim() !== '') {
          contentObj[snakeKey] = value;
        }
      });

      // 构建 payload
      const payload = {
        medmstrId: this.currentPatient.id,
        professionalId: localStorage.getItem('userId') || this.userInfo?.id,
        departmentId: this.getCurrentDepartmentId(),
        copyFlag: copyFlag, // 标识是否为创建副本
        visitNum: this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || '',
        content: JSON.stringify(contentObj) // 动态字段的 JSON
      };

      // 如果 chatId 不为空，则添加到请求参数中
      if (this.currentPatient?.chatId && this.currentPatient.chatId.trim() !== '') {
        payload.chatId = this.currentPatient.chatId;
      }

      console.log('保存病历数据:', {
        activeArchives: this.activeArchives,
        payload: payload
      });

      this.isSavingArchives = true;

      try {
        const resp = await request({
          url: '/ly/archive/add',
          method: 'POST',
          data: payload
        });
        if (resp && resp.success) {
          console.log('病历保存成功');
          return resp;
        } else {
          this.$message.error(resp?.message || '病历存档失败');
          return null;
        }
      } catch (e) {
        this.$message.error('网络错误，病历存档失败');
        return null;
      } finally {
        // 无论成功或失败，都要重置保存状态
        this.isSavingArchives = false;
      }
    },

    // 提交HIS
    async handleSubmitHis() {
      try {
        this.hisSubmitLoading = true;

        // 检查是否有患者和病历内容
        if (!this.currentPatient?.id) {
          this.$message.warning('请先选择患者');
          return;
        }

        // 检查是否有病历字段或录音内容
        const hasArchives = this.activeArchives && this.activeArchives.length > 0 &&
                            this.activeArchives.some(item => item.value && item.value.trim() !== '');
        const hasAudioContent = this.audioMessage || this.extractedMessage;

        if (!hasArchives && !hasAudioContent) {
          this.$message.warning('暂无病历内容可提交');
          return;
        }

        // 步骤1：获取需要提交HIS的字段配置
        const professionalId = localStorage.getItem('userId') || this.userInfo?.id;
        if (!professionalId) {
          this.$message.warning('未找到医生ID');
          return;
        }

        console.log('步骤1：获取HIS字段配置');
        const departmentId = this.getCurrentDepartmentId();
        if (!departmentId) {
          this.$message.error('未找到科室ID');
          return;
        }

        const hisFieldsResp = await request({
          url: '/ly/archive/fields/template',
          method: 'POST',
          data: {
            professionalId: professionalId,
            departmentId: departmentId,
            showLocation: 'HIS' // 获取HIS字段配置
          }
        });

        if (!hisFieldsResp || !hisFieldsResp.success || !hisFieldsResp.result) {
          this.$message.error('获取HIS字段配置失败');
          return;
        }

        const hisFields = hisFieldsResp.result.fields || [];
        const hisFieldKeys = hisFields.map(f => f.key); // 获取需要提交的字段key列表（下划线命名）
        console.log('需要提交HIS的字段:', hisFieldKeys);

        // 如果没有配置HIS字段，提示用户
        if (hisFieldKeys.length === 0) {
          this.$message.warning('当前科室未配置需要提交HIS的字段，无法提交');
          return;
        }

        // 步骤2：获取HIS提交接口地址
        console.log('步骤2：获取HIS提交接口地址');
        const hisUrlResp = await request({
          url: '/ly/careProfessionals/queryHisUrlById',
          method: 'GET',
          params: { id: professionalId }
        });

        if (!hisUrlResp || !hisUrlResp.success || !hisUrlResp.result) {
          this.$message.error('获取HIS接口地址失败');
          return;
        }

        const hisUrl = hisUrlResp.result;
        console.log('获取到HIS接口地址:', hisUrl);

        // 步骤3：构建提交数据
        // 优先从已保存的最新病历构建提交数据，失败则回退到本地 activeArchives 构建
        let submitData = await this.buildHisSubmitDataFromArchive(hisFieldKeys);
        if (!submitData) {
          submitData = this.buildHisSubmitData(hisFieldKeys);
        }
        // HIS 侧医生与科室 ID（来自患者数据）
        submitData.hisProfessionalId = this.currentPatient.hisProfessionalId || '';
        submitData.hisDepartmentId = this.currentPatient.hisDepartmentId || '';
        console.log('构建的提交数据:', submitData);

        // 步骤4：插入提交前日志
        console.log('步骤4：插入提交前日志');
        const logResp = await request({
          url: '/ly/archive/addLogForHisCommitBefore',
          method: 'POST',
          data: submitData
        });

        if (!logResp || !logResp.success) {
          this.$message.error('记录提交日志失败');
          return;
        }

        console.log('提交前日志记录成功');

        // 步骤5：调用HIS接口提交数据
        console.log('步骤5：调用HIS接口提交数据');
        const hisSubmitResp = await this.submitToHis(hisUrl, submitData);

        if (!hisSubmitResp) {
          this.$message.error('HIS系统提交失败');
          if (DEMO_CLOSE_ON_HIS_SUBMIT_ERROR) {
            this.$message.warning('（联调）未拿到 HIS 成功结果，2 秒后仍关闭窗口以便验证流程');
            setTimeout(() => this.closeModal(), 2000);
          }
          return;
        }

        console.log('HIS系统提交成功:', hisSubmitResp);

        // 步骤6：记录HIS返回数据
        console.log('步骤6：记录HIS返回数据');
        const hisResultResp = await request({
          url: '/ly/archive/addLogForHisCommitBefore',
          method: 'POST',
          data: {
            ...submitData,
            hisResponse: JSON.stringify(hisSubmitResp),
            submitStatus: 'success'
          }
        });

        if (hisResultResp && hisResultResp.success) {
          this.hisSubmitted = true;
          this.$message.success('病历已成功提交到HIS系统');

          // 2秒后自动关闭窗口
          setTimeout(() => {
            this.closeModal();
          }, 2000);
        } else {
          this.$message.warning('HIS提交成功，但记录返回数据失败');
        }

      } catch (error) {
        console.error('HIS提交过程出错:', error);
        this.$message.error('HIS提交失败：' + (error.message || '未知错误'));
        if (DEMO_CLOSE_ON_HIS_SUBMIT_ERROR) {
          this.$message.warning('（联调）HIS 调用异常，2 秒后关闭窗口以便验证流程');
          setTimeout(() => this.closeModal(), 2000);
        }
      } finally {
        this.hisSubmitLoading = false;
      }
    },

    // 构建HIS提交数据
    buildHisSubmitData(hisFieldKeys = []) {
      const professionalId = localStorage.getItem('userId') || this.userInfo?.id;
      const visitNum = this.currentPatient?.visitNum || localStorage.getItem('visitNum') || '';

      // 基础数据
      const submitData = {
        consultationId: '',
        medmstrId: this.currentPatient.id,
        visitNum: visitNum,
        visitDateStr: new Date().toISOString().split('T')[0],
        patientName: this.currentPatient.realname || '',
        patientPhone: this.currentPatient.phone || '',
        professionalId: professionalId,
        chatId: this.currentPatient.chatId || '',
        source: 'jump_modal'
      };

      // 从activeArchives中筛选出需要提交HIS的字段，并转换为驼峰命名
      // activeArchives中的key是下划线命名（如 chief_complaint），需要转换为驼峰命名（如 chiefComplaint）
      if (Array.isArray(this.activeArchives) && this.activeArchives.length > 0) {
        this.activeArchives.forEach(item => {
          // 只处理在HIS字段列表中的字段
          if (hisFieldKeys.includes(item.key)) {
            // 将下划线命名转换为驼峰命名
            const camelKey = this.snakeToCamel(item.key);
            submitData[camelKey] = item.value || '';
          }
        });
      } else {
        // 如果没有病历字段，则将识别内容作为主诉（如果主诉在HIS字段列表中）
        const extractedContent = this.extractedMessage || this.audioMessage || '';
        if (extractedContent && (hisFieldKeys.length === 0 || hisFieldKeys.includes('chief_complaint'))) {
          submitData.chiefComplaint = extractedContent;
        }
      }

      console.log('构建的HIS提交数据:', submitData);
      return submitData;
    },

    // 从最新病历记录构建HIS提交数据（优先使用已经保存到后端的内容）
    async buildHisSubmitDataFromArchive(hisFieldKeys = []) {
      const professionalId = localStorage.getItem('userId') || this.userInfo?.id;
      const medmstrId = this.currentPatient?.id;
      const visitNum = this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || '';
      const departmentId = this.getCurrentDepartmentId();

      if (!professionalId || !medmstrId || !visitNum || !departmentId) {
        console.warn('缺少必要参数，无法从病历构建HIS提交数据', { professionalId, medmstrId, visitNum, departmentId });
        return null;
      }

      try {
        // 1) 取最新病历列表
        const historyResp = await request({
          url: '/ly/archive/history',
          method: 'POST',
          data: {
            medmstrId: medmstrId,
            onlyMine: true,
            professionalId: professionalId,
            pageNum: 1,
            pageSize: 1,
            visitNum: visitNum
          }
        });

        const latest = Array.isArray(historyResp?.result) ? historyResp.result[0] : null;
        if (!latest?.id) {
          console.warn('未找到最新病历记录');
          return null;
        }

        // 2) 查详情
        const detailResp = await request({
          url: '/ly/archive/history/detail',
          method: 'POST',
          data: {
            recordId: latest.id,
            departmentId: departmentId
          }
        });

        if (!detailResp || !detailResp.success || !detailResp.result) {
          console.warn('获取病历详情失败', detailResp);
          return null;
        }

        const fields = Array.isArray(detailResp.result.fields) ? detailResp.result.fields : [];
        const medicalDetail = detailResp.result.medicalDetail || {};

        // 3) 组装 submitData
        const submitData = {
          consultationId: medicalDetail.consultationId || '',
          medmstrId: medmstrId,
          visitNum: visitNum,
          visitDateStr: medicalDetail.visitDate || new Date().toISOString().split('T')[0],
          patientName: this.currentPatient?.realname || this.currentPatient?.name || '',
          patientPhone: this.currentPatient?.phone || '',
          professionalId: professionalId,
          chatId: this.currentPatient?.chatId || '',
          source: 'jump_modal'
        };

        // 4) fields 转驼峰并填充，仅填有值的字段
        fields.forEach(f => {
          const snakeKey = f.key || '';
          const val = f.value || '';
          if (!val || !snakeKey) return;
          const camelKey = this.snakeToCamel(snakeKey);
          submitData[camelKey] = val;
        });

        console.log('使用病历详情构建的HIS提交数据:', submitData);
        return submitData;
      } catch (err) {
        console.error('从病历构建HIS提交数据失败:', err);
        return null;
      }
    },

    /* 下划线命名转驼峰命名 */
    snakeToCamel(str) {
      return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
    },

    // 提交到HIS系统（与 jumpModal-copy 一致：无 Abort 超时）
    async submitToHis(hisUrl, submitData) {
      try {
        console.log('提交数据:', submitData);

        // 使用fetch进行跨域请求
        const response = await fetch(hisUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(submitData),
          mode: 'cors',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HIS接口请求失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('HIS接口返回结果:', result);
        return result;
      } catch (error) {
        console.error('HIS接口调用失败:', error);
        throw error;
      }
    },

    // 处理遮罩点击
    handleOverlayClick() {
      // 可以选择是否允许点击遮罩关闭
      // this.closeModal();
    },

    // 关闭页面
    closeModal() {
      // 清除store token
      this.clearStoreToken();

      // 尝试关闭浏览器窗口（适用于通过 window.open 打开的 jumpModal 页面）
      try {
        // 检查是否是通过 window.open 打开的窗口
        if (window.opener) {
          console.log('检测到是通过 window.open 打开的窗口，自动关闭');
          window.close();
        } else {
          // 如果无法关闭窗口，则触发关闭事件（适用于模态窗口场景）
          this.$emit('update:visible', false);
          this.$emit('close');
        }
      } catch (error) {
        console.warn('关闭窗口失败:', error);
        // 即使关闭窗口失败，也要触发关闭事件
        this.$emit('update:visible', false);
        this.$emit('close');
      }

      // 重置状态
      this.resetState();
    },

    // 清除store token
    clearStoreToken() {
      try {
        // 清除相关token
        localStorage.removeItem('storetoken');
        sessionStorage.removeItem('storetoken');

        // 如果使用了Vuex，可以分发清除action
        // this.$store.dispatch('auth/clearToken');

        console.log('已清除store token');
      } catch (error) {
        console.error('清除store token失败:', error);
      }
    },

    // 获取当前科室ID
    getCurrentDepartmentId() {
      return (
        this.currentPatient?.departmentId ||
        localStorage.getItem('currentPatientDepartmentId') ||
        ''
      );
    },

    // 处理录音内容，分解为病历字段
    async processRecordingToArchives(includeMedicalRecords = false) {
      this.archivesProcessing = true;
      this.archivesProcessed = false;

      try {
        const parsedContent = this.extractedMessage || this.audioMessage;
        if (!parsedContent) {
          console.warn('没有录音内容可处理');
          this.archivesProcessing = false;
          this.archivesProcessed = true; // 即使没有内容也标记为完成
          return;
        }

        // 确保存在 ChatId
        const chatId = await this.ensureChatId();
        if (!chatId) {
          console.error('会话创建失败，无法处理病历字段');
          this.$message.warning('会话创建失败，将使用原始内容作为主诉');
          this.archivesProcessing = false;
          this.archivesProcessed = true; // 标记为完成，允许提交原始内容
          return;
        }

        // 调用AI接口分解病历字段
        const professionalId = localStorage.getItem('userId') || this.userInfo?.id;
        const payload = {
          chatId: chatId,
          content: parsedContent,
          medmstrId: this.currentPatient?.id || '',
          professionalId: professionalId,
          departmentId: this.getCurrentDepartmentId(),
          visitNum: this.currentPatient?.visitNum || this.visitNum || localStorage.getItem('visitNum') || '',
          includeMedicalRecords: includeMedicalRecords,
          includeTestReports: false,
          includeHistoryChat: false
        };

        const resp = await request({
          url: '/ly/archive/create',
          method: 'POST',
          data: payload
        });

        if (resp && resp.success) {
          const suggestionFields = (resp.result && resp.result.fields) || [];
          console.log('API返回的建议字段:', suggestionFields);
          // 创建病历字段并合并AI建议
          suggestionFields.forEach(field => {
            this.activeArchives.push({
              key: field.key,
              value: field.suggestedValue,
            });
          });
          
          // 自动全部接受AI建议
          setTimeout(() => {
            try {
              // 第一步：执行AI建议接受逻辑
              this.archivesProcessing = false;
              this.archivesProcessed = true;
              this.$message.success('录音内容已分解为病历字段');

              // 第二步：立即执行保存病历操作（按顺序执行，不会跳过）
              console.log('===== 开始保存病历到数据库 =====');
              // 注意：saveArchives 是异步函数，需要用 await/then 处理
              this.saveArchives().then((saveResult) => {
                if (!saveResult) {
                  this.$message.warning('病历保存失败');
                } else {
                  // 可选：保存成功的提示
                  this.$message.success('病历保存成功');
                }
              }).catch((error) => {
                // 捕获保存病历的异常，避免代码卡死
                console.error('保存病历时出错：', error);
                this.$message.error('病历保存异常，请重试');
              });
            } catch (timerError) {
              // 捕获定时器内所有代码的异常
              console.error('定时器内执行逻辑出错：', timerError);
              this.$message.error('操作失败，请检查日志');
            }
          }, 500);

        } else {
          console.error('AI处理失败:', resp?.message);
          this.$message.warning('AI处理失败，将使用原始内容作为主诉');
          this.archivesProcessing = false;
          this.archivesProcessed = true; // 即使失败也标记为完成
        }
      } catch (error) {
        console.error('处理录音内容失败:', error);
        this.$message.warning('AI处理失败，将使用原始内容作为主诉');
        this.archivesProcessing = false;
        this.archivesProcessed = true; // 即使异常也标记为完成
      }
    },

    // 创建标准空字段
    createStandardEmptyArchives() {
      const standardFields = [
        { key: 'chiefComplaint', title: '主诉', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'presentIllness', title: '现病史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'pastHistory', title: '既往史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'allergyHistory', title: '过敏史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'familyHistory', title: '家族史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'marriageBearingHistory', title: '婚育史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'tobaccoAlcoholHistory', title: '烟酒史', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'vitalSigns', title: '生命体征', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'physicalExamination', title: '体格检查', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'primaryDiagnosis', title: '诊断', value: '', isEdit: false, originalValue: '', isEdit_newValue: null },
        { key: 'treatmentPlan', title: '治疗计划', value: '', isEdit: false, originalValue: '', isEdit_newValue: null }
      ];
      return standardFields;
    },

    // 创建空字段并填充AI建议
    createEmptyArchivesWithSuggestions(suggestionFields) {
      // 先创建标准的空字段
      this.activeArchives = this.createStandardEmptyArchives();

      if (!suggestionFields || suggestionFields.length === 0) {
        console.log('没有AI建议，只创建空字段');
        return;
      }

      // 创建字段映射
      const map = new Map();
      this.activeArchives.forEach(item => {
        map.set(item.key, item);
        map.set(item.title, item);
      });

      // 处理AI建议，设置为建议模式
      suggestionFields.forEach(suggestion => {
        const fieldName = suggestion.key;
        const suggestedValue = suggestion.suggestedValue || suggestion.value || suggestion.content || '';
        const fieldLabel = suggestion.label || fieldName;

        console.log('处理AI建议字段:', fieldName, '标签:', fieldLabel, '建议值:', suggestedValue);

        // 查找匹配的字段
        let item = map.get(fieldName);
        if (!item) {
          // 尝试通过映射匹配
          const mappedName = this.getFieldMapping(fieldName);
          item = map.get(mappedName);
        }

        if (item) {
          // 设置为建议模式：原始值为空，建议值在isEdit_newValue中
          item.value = '';
          item.originalValue = '';
          item.isEdit_newValue = suggestedValue;
          console.log(`已为字段 ${item.title} 设置AI建议:`, suggestedValue);
        } else {
          console.warn(`未找到匹配的字段: ${fieldName}`);
        }
      });

      console.log('已创建病历字段并设置AI建议:', this.activeArchives);
    },

    // 字段名称映射
    getFieldMapping(fieldName) {
      const mapping = {
        '主诉': 'chiefComplaint',
        '现病史': 'presentIllness',
        '既往史': 'pastHistory',
        '过敏史': 'allergyHistory',
        '家族史': 'familyHistory',
        '婚育史': 'marriageBearingHistory',
        '烟酒史': 'tobaccoAlcoholHistory',
        '生命体征': 'vitalSigns',
        '体格检查': 'physicalExamination',
        '诊断': 'primaryDiagnosis',
        '治疗计划': 'treatmentPlan',
        // 下划线命名映射到本地驼峰字段
        'chief_complaint': 'chiefComplaint',
        'present_illness': 'presentIllness',
        'past_history': 'pastHistory',
        'allergy_history': 'allergyHistory',
        'family_history': 'familyHistory',
        'marriage_bearing_history': 'marriageBearingHistory',
        'tobacco_alcohol_history': 'tobaccoAlcoholHistory',
        'vital_signs': 'vitalSigns',
        'physical_examination': 'physicalExamination',
        'primary_diagnosis': 'primaryDiagnosis',
        'treatment_plan': 'treatmentPlan'
      };
      return mapping[fieldName] || fieldName;
    },

    // 驼峰转下划线（用于提交后端字段）
    toSnakeCase(camelStr) {
      if (!camelStr) return '';
      return camelStr.replace(/([A-Z])/g, '_$1').toLowerCase();
    },

    // 全部接受AI建议
    handleAcceptAll() {
      this.activeArchives.forEach(item => {
        if (item.isEdit_newValue !== null && item.isEdit_newValue !== undefined) {
          item.value = item.isEdit_newValue;
          item.originalValue = item.isEdit_newValue;
          item.isEdit_newValue = null;
        }
      });
      this.$forceUpdate();
    },

    // 保存失败的录音记录到存储
    async saveFailedRecordingToStorage(audioBlob, metadata) {
      try {
        const { saveFailedRecording } = await import('@/utils/recordingStorage');
        await saveFailedRecording(audioBlob, metadata);
        console.log('失败的录音已保存，可以稍后重试');
      } catch (error) {
        console.error('保存失败录音记录出错：', error);
      }
    },

    // 重置状态
    resetState() {
      this.currentPatient = null;
      this.audioMessage = '';
      this.extractedMessage = '';
      this.tipMessage = '';
      this.recordingCompleted = false;
      this.hisSubmitted = false;
      this.hisSubmitLoading = false;
      this.patientSelected = false;
      this.activeArchives = [];
      this.isSavingArchives = false;
      this.archivesProcessing = false;
      this.archivesProcessed = false;
      this.patientDataError = false;
      this.errorMessage = '';
      this.includeMedicalRecords = false;
    }
  }
};
</script>

<style scoped>
/* 主页面容器：填满 management 的 content-wrapper（1000×600 小窗时由父级高度约束） */
.jump-modal-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 图标文本 */
.icon-text {
  font-size: 16px;
  font-weight: normal;
}

.btn-icon {
  margin-right: 4px;
  font-size: 14px;
}

/* 错误状态 */
.error-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.error-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.error-text {
  font-size: 16px;
  color: #f56c6c;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 24px;
  word-break: break-word;
  max-width: 400px;
}

.error-actions {
  display: flex;
  justify-content: center;
}

/* 成功图标 */
.success-icon {
  font-size: 48px;
  color: #67c23a;
  margin-bottom: 16px;
  font-weight: bold;
}

/* 页面内容区域 */
.page-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 16px 20px 14px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* 患者信息 */
.patient-info {
  flex-shrink: 0;
  margin-bottom: 12px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.info-row .label {
  color: #666;
  font-weight: 500;
}

.info-row .value {
  color: #333;
  font-weight: 600;
  margin-left: 4px;
}

.info-row .separator {
  margin: 0 12px;
  color: #ccc;
  font-weight: normal;
}

/* 录音区域 */
.recording-section {
  flex-shrink: 0;
  margin-bottom: 12px;
}

/* 核心极简样式：一行显示；交叉轴居中对齐，避免「提交HIS」与「追加录音」上下错位 */
.sound-recording-wrapper {
  display: flex;
  flex-wrap: nowrap;
  align-items: center; /* 与右侧录音组件垂直居中对齐（修复 1000×600 下按钮不齐） */
  gap: 12px;
  justify-content: center;
  width: 100%;
}

/* 穿透：录音组件自带 padding:20px 0，会在同行内把右侧垫高；此处收紧并与左侧按钮区对齐 */
.sound-recording-wrapper :deep(.jump-modal-sound-recording) {
  width: auto !important;
  flex: 0 1 auto;
  max-width: 180px;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  justify-content: center;
}

/* 录音结果：占据剩余高度，双栏内滚动 */
.recording-result {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
}

.recording-result > .processing-tip {
  flex-shrink: 0;
}

.result-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
}

.result-column {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.section-title {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  margin-top: 0;
}

.section-title i {
  margin-right: 25px;
  margin-right: 8px;
  color: #409eff;
}

/* 病历处理中提示 */
.processing-tip {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-bottom: 12px;
  background-color: #f0f9ff;
  border: 1px solid #409eff;
  border-radius: 6px;
  color: #409eff;
  font-size: 14px;
  font-weight: 500;
}

.processing-tip .loading-icon {
  margin-right: 8px;
  font-size: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 提取内容框 */
.extracted-content-box {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #f0f9ff;
  border-radius: 8px;
  padding: 8px;
  border: 2px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.extracted-text-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
}

.extracted-content {
  margin-bottom: 0;
}

.content-label {
  font-size: 14px;
  color: #409eff;
  margin-bottom: 8px;
  font-weight: 600;
}

.extracted-text {
  font-size: 18px;
  color: #333;
  line-height: 1.8;
  word-break: break-word;
  font-weight: 500;
  white-space: pre-wrap;
  max-height: none;
  overflow: visible;
}

/* 操作按钮 */
.action-buttons {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  margin-top: 0;
  margin-bottom: 8px;
  padding: 4px 0;
}

.action-buttons .el-button {
  min-width: 120px;
}

.submit-button-large-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  max-width: 100%;
  padding: 0;
}

/* 大尺寸提交按钮 */
.submit-button-large {
  font-size: 14px !important;
  padding: 0.25rem 22px !important;
  height: 43.33px;
  min-width: 150px !important;
  font-weight: 500 !important;
  border-radius: 9999px !important;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3) !important;
  transition: all 0.3s ease !important;
  align-items: center; /* 垂直居中 */
}

.submit-button-large:hover {
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4) !important;
  transform: translateY(-2px) !important;
}

.submit-button-large .btn-icon {
  font-size: 20px !important;
  margin-right: 8px !important;
}

/* 提交成功 */
.submit-success {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.submit-success i {
  font-size: 48px;
  color: #67c23a;
  margin-bottom: 16px;
}

.success-text {
  font-size: 16px;
  color: #67c23a;
  font-weight: 600;
}

/* 小窗口专用 Loading 样式 */
.jump-modal-loading {
  border-radius: 8px !important;
  padding: 20px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.jump-modal-loading .el-loading-spinner {
  margin-bottom: 12px !important;
}

.jump-modal-loading .el-loading-text {
  font-size: 14px !important;
  color: #333 !important;
}

.jump-modal-loading .el-icon-loading {
  font-size: 24px !important;
  color: #4169e1 !important;
}

@media (max-width: 599px) {
  .page-content {
    max-width: 900px;
    padding: 12px 12px 10px;
  }

  .result-layout {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
